/**
 * APEX v7.7.3 — Game-level Insights : Catégories C/D/E/F
 *
 * C : Itemization (C1 v2, C2, C4, C6 tier2, C8)
 * D : Vision (D1, D2, D3, D5, D6)
 * E : Spike niveau (E1, E2, E3, E4)
 * F : Draft/matchups (F1 v2, F2 v2, F5) — comparaison à TES propres stats
 *
 * Principe structurant (validé) : PAS DE TABLES HARDCODÉES.
 * Les comparaisons "build optimal / matchup" se font sur TA propre baseline.
 *
 * Voir docs/PRD_OBJECTIVES_V7.7_FINAL.md sections 13.4 à 13.7.
 */

// IDs de bottes (DataDragon) — données stables Riot, pas une "table méta"
const BOOTS_IDS = new Set([3047, 3158, 3009, 3006, 3020, 3111, 3117, 3010]);
const CONTROL_WARD_ID = 2055;

function isCancelled(game) {
  if (!game) return false;
  if (game.isRemake === true) return true;
  if (typeof game.duration === 'number' && game.duration > 0 && game.duration < 5) return true;
  return false;
}

function eventSeconds(ev) {
  return (ev.minute || 0) * 60 + (ev.second || 0);
}

// ============================================================
// C2 — Bottes en retard
// ============================================================

function detectLateBoots(game, context) {
  const items = game.itemEvents || [];
  const bootsPurchase = items.find(e =>
    e.action === 'purchase' && BOOTS_IDS.has(e.itemId)
  );
  if (!bootsPurchase) {
    // Aucune botte achetée de toute la game (rare, souvent = oubli)
    if (items.length > 0 && (game.duration || 0) > 18 * 60) {
      return {
        id: 'C2_no_boots',
        category: 'C',
        severity: 'warning',
        title: 'Aucune botte achetée',
        short: 'Pas de bottes sur la game',
        long: 'Tu n\'as pas acheté de bottes de toute la game. Mouvement compromis — sauf build très spécifique, c\'est rarement optimal.',
        context: {}
      };
    }
    return null;
  }

  const min = bootsPurchase.minute;
  if (min > 15) {
    // Contextualisation : si CS/min très élevé, c'est un choix viable
    const csPerMin = game.csPerMin || 0;
    const isHighCs = csPerMin >= 9;
    return {
      id: 'C2_late_boots',
      category: 'C',
      severity: isHighCs ? 'info' : 'warning',
      title: 'Bottes en retard',
      short: `Bottes craft à ${min}min`,
      long: isHighCs
        ? `Bottes craft à ${min}min — tardif, mais ton CS/min de ${csPerMin.toFixed(1)} suggère que tu as privilégié ton item core. Choix défendable.`
        : `Bottes craft à ${min}min (typiquement avant 12min). Mouvement et kite compromis en mid-game.`,
      context: { boots_minute: min, cs_per_min: csPerMin }
    };
  }
  return null;
}

// ============================================================
// C1 v2 — Premier item core vs TA baseline
// ============================================================

function detectCoreItemTiming(game, context) {
  const items = (game.itemEvents || []).filter(e => e.action === 'purchase');
  if (items.length === 0) return null;

  // Heuristique "item core" : premier achat > 2500 gold value n'est pas dispo ici
  // sans table de prix. On approxime : le 1er gros item = le 3e+ achat distinct
  // après les composants de départ. On se base sur la baseline user si fournie.
  const baseline = context?.userStats?.coreItemMinuteByChampion?.[game.champion];

  // Sans baseline, on ne déclenche pas (on ne veut pas de table hardcodée de prix)
  if (typeof baseline !== 'number') return null;

  // Le "core" approximé = le 4e achat (après 3 composants/starter)
  const purchases = items.sort((a, b) => eventSeconds(a) - eventSeconds(b));
  if (purchases.length < 4) return null;
  const coreApprox = purchases[3];
  const coreMin = coreApprox.minute;

  const delta = coreMin - baseline;
  if (delta >= 2) {
    return {
      id: 'C1_core_item_late_vs_baseline',
      category: 'C',
      severity: 'warning',
      title: 'Item core retardé',
      short: `Core à ~${coreMin}min (ta moyenne ${baseline.toFixed(0)}min)`,
      long: `Ton item core est arrivé vers ${coreMin}min, soit ${delta.toFixed(0)}min plus tard que ta moyenne habituelle sur ${game.champion} (${baseline.toFixed(0)}min). Spike retardé.`,
      context: { core_minute: coreMin, baseline }
    };
  }
  return null;
}

// ============================================================
// C4 — Pas de control ward en mid-game
// ============================================================

function detectNoControlWard(game) {
  const items = game.itemEvents || [];
  const dur = game.duration || 0;
  if (dur < 25 * 60) return null; // game trop courte pour conclure

  const controlWards = items.filter(e =>
    e.action === 'purchase' && e.itemId === CONTROL_WARD_ID &&
    e.minute >= 10 && e.minute <= 25
  );

  if (controlWards.length === 0) {
    return {
      id: 'C4_no_control_ward',
      category: 'C',
      severity: 'info',
      title: 'Pas de control ward',
      short: '0 control ward (10-25min)',
      long: 'Aucune control ward achetée entre 10 et 25min. Ce n\'est pas le rôle principal de l\'ADC, mais 1-2 pinks aident le contrôle de vision sur objectifs.',
      context: {}
    };
  }
  return null;
}

// ============================================================
// C8 — Build vs comp adverse
// ============================================================

function detectBuildVsComp(game, context) {
  // Profil dégâts adverse : on a besoin du split AD/AP reçu.
  // Disponible via game.opponentStats ou game.damageByType si extrait.
  const physTaken = game.physicalDamageTaken ?? game.myStats?.physicalDamageTaken;
  const magicTaken = game.magicDamageTaken ?? game.myStats?.magicDamageTaken;

  if (typeof physTaken !== 'number' || typeof magicTaken !== 'number') return null;
  const total = physTaken + magicTaken;
  if (total < 1000) return null;

  const physRatio = physTaken / total;

  // Inventaire défensif : on regarde les items finaux
  const finalItems = Array.isArray(game.items) ? game.items : [];

  // IDs défensifs anti-AD (armure) / anti-AP (MR) — données stables DataDragon
  const ARMOR_ITEMS = new Set([3047, 3110, 3143, 3193, 6662, 3075, 3076]);
  const MR_ITEMS = new Set([3111, 3102, 3091, 3156, 4637, 3140, 3814]);

  const hasArmor = finalItems.some(id => ARMOR_ITEMS.has(id));
  const hasMr = finalItems.some(id => MR_ITEMS.has(id));

  // Comp adverse très AD et tu n'as aucune armure
  if (physRatio >= 0.75 && !hasArmor) {
    return {
      id: 'C8_no_armor_vs_ad',
      category: 'C',
      severity: game.result === 'L' ? 'warning' : 'info',
      title: 'Build vs comp adverse',
      short: `${Math.round(physRatio * 100)}% dégâts AD subis, 0 armure`,
      long: `Tu as pris ${Math.round(physRatio * 100)}% de tes dégâts en AD mais aucun item d'armure. Plate Steelcaps ou un défensif auraient mitigé une partie du burst.`,
      context: { phys_ratio: physRatio }
    };
  }
  // Comp adverse très AP et tu n'as aucune MR
  if (physRatio <= 0.25 && !hasMr) {
    return {
      id: 'C8_no_mr_vs_ap',
      category: 'C',
      severity: game.result === 'L' ? 'warning' : 'info',
      title: 'Build vs comp adverse',
      short: `${Math.round((1 - physRatio) * 100)}% dégâts AP subis, 0 MR`,
      long: `Tu as pris ${Math.round((1 - physRatio) * 100)}% de tes dégâts en AP mais aucun item de résistance magique. Mercury's ou un défensif MR auraient aidé.`,
      context: { phys_ratio: physRatio }
    };
  }
  return null;
}

// ============================================================
// D1 — Wards posées
// ============================================================

function detectWardsPlaced(game, context) {
  const wards = game.wardEvents || [];
  const placed = wards.filter(w => w.action === 'placed').length;

  // Fallback sur myStats.wardsPlaced si pas d'events
  const wardsPlaced = placed > 0 ? placed : (game.myStats?.wardsPlaced || 0);
  if (wardsPlaced === 0 && (game.duration || 0) < 15 * 60) return null;

  // Comparaison à TA baseline si dispo, sinon seuil ADC générique (8)
  const baseline = context?.userStats?.avgWardsPlaced;
  const threshold = typeof baseline === 'number' ? baseline * 0.6 : 8;

  if (wardsPlaced < threshold) {
    return {
      id: 'D1_low_wards',
      category: 'D',
      severity: 'info',
      title: 'Peu de wards posées',
      short: `${wardsPlaced} wards${typeof baseline === 'number' ? ` (ta moyenne ${baseline.toFixed(0)})` : ''}`,
      long: `${wardsPlaced} wards posées sur la game${typeof baseline === 'number' ? `, sous ta moyenne de ${baseline.toFixed(0)}` : ' (moyenne ADC ≈ 12)'}. Contribuer à la vision réduit les morts en rotation.`,
      context: { wards_placed: wardsPlaced, baseline }
    };
  }
  return null;
}

// ============================================================
// D6 — Vision score vs TA baseline (positif/négatif)
// ============================================================

function detectVisionScoreVsBaseline(game, context) {
  const vs = game.myStats?.visionScore || 0;
  const baseline = context?.userStats?.avgVisionScore;
  if (typeof baseline !== 'number' || baseline <= 0) return null;

  if (vs >= baseline * 1.4 && vs > 0) {
    return {
      id: 'D6_high_vision',
      category: 'D',
      severity: 'positive',
      title: 'Excellente vision',
      short: `Vision score ${Math.round(vs)} (ta moy. ${baseline.toFixed(0)})`,
      long: `Vision score de ${Math.round(vs)} cette game, bien au-dessus de ta moyenne de ${baseline.toFixed(0)}. Le genre de game à reproduire.`,
      context: { vision_score: vs, baseline }
    };
  }
  if (vs <= baseline * 0.5) {
    return {
      id: 'D6_low_vision',
      category: 'D',
      severity: 'info',
      title: 'Vision faible',
      short: `Vision score ${Math.round(vs)} (ta moy. ${baseline.toFixed(0)})`,
      long: `Vision score de ${Math.round(vs)}, sous la moitié de ta moyenne (${baseline.toFixed(0)}). Pense aux pinks et au sweep en rotation.`,
      context: { vision_score: vs, baseline }
    };
  }
  return null;
}

// ============================================================
// E1/E2/E3 — Lead/deficit niveau aux paliers clés
// ============================================================

function levelAtTime(levelEvents, targetSec) {
  if (!Array.isArray(levelEvents) || levelEvents.length === 0) return null;
  let lvl = 1;
  for (const e of levelEvents) {
    if (eventSeconds(e) <= targetSec) lvl = e.level;
    else break;
  }
  return lvl;
}

function detectLevelSpikes(game) {
  const mine = game.levelEvents || [];
  const opp = game.oppLevelEvents || [];
  if (mine.length === 0 || opp.length === 0) return null;

  const insights = [];
  const checkpoints = [
    { sec: 6 * 60 + 30, label: '6:30', skill: 'niveau 6 (ult R1)' },
    { sec: 11 * 60 + 30, label: '11:30', skill: 'niveau 11 (ult R2)' },
    { sec: 16 * 60 + 30, label: '16:30', skill: 'niveau 16 (ult R3)' }
  ];

  for (const cp of checkpoints) {
    const myLvl = levelAtTime(mine, cp.sec);
    const oppLvl = levelAtTime(opp, cp.sec);
    if (myLvl === null || oppLvl === null) continue;
    const diff = myLvl - oppLvl;
    if (diff <= -2) {
      insights.push({
        id: `E_level_deficit_${cp.label}`,
        category: 'E',
        severity: 'warning',
        title: `Retard de niveau à ${cp.label}`,
        short: `Niv ${myLvl} vs ${oppLvl} adverse`,
        long: `À ${cp.label}, tu étais niveau ${myLvl} contre ${oppLvl} pour ton lane opp. Tu as subi son ${cp.skill}. CS/XP à sécuriser.`,
        context: { my_level: myLvl, opp_level: oppLvl, checkpoint: cp.label }
      });
      break; // un seul insight de spike suffit
    }
    if (diff >= 2) {
      insights.push({
        id: `E_level_lead_${cp.label}`,
        category: 'E',
        severity: 'positive',
        title: `Avance de niveau à ${cp.label}`,
        short: `Niv ${myLvl} vs ${oppLvl} adverse`,
        long: `À ${cp.label}, tu avais ${myLvl} contre ${oppLvl} pour l'adversaire — fenêtre de spike à exploiter (all-in / prio).`,
        context: { my_level: myLvl, opp_level: oppLvl, checkpoint: cp.label }
      });
      break;
    }
  }
  return insights;
}

// ============================================================
// F1 v2 / F2 v2 — Matchup difficile/favorable pour TOI
// ============================================================

function detectMatchupVsBaseline(game, context) {
  const enemyAdc = game.enemyAdc;
  if (!enemyAdc || !game.champion) return null;

  const allGames = context?.allGames;
  if (!Array.isArray(allGames) || allGames.length < 4) return null;

  // Tes games sur ce matchup précis (ton champ vs cet enemy ADC)
  const matchupGames = allGames.filter(g =>
    !isCancelled(g) &&
    g.champion === game.champion &&
    g.enemyAdc === enemyAdc
  );
  if (matchupGames.length < 4) return null;

  const wins = matchupGames.filter(g => g.result === 'W').length;
  const wr = Math.round((wins / matchupGames.length) * 100);

  if (wr < 40) {
    return {
      id: 'F1_hard_matchup_for_you',
      category: 'F',
      severity: 'warning',
      title: 'Matchup difficile pour toi',
      short: `${game.champion} vs ${enemyAdc} : ${wr}% WR`,
      long: `Tu as ${wr}% de winrate sur ${game.champion} vs ${enemyAdc} (${matchupGames.length} games). C'est un matchup que tu perds statistiquement — ban ou pick différent à considérer.`,
      context: { wr, games: matchupGames.length }
    };
  }
  if (wr > 60) {
    return {
      id: 'F2_favorable_matchup_for_you',
      category: 'F',
      severity: 'positive',
      title: 'Matchup favorable pour toi',
      short: `${game.champion} vs ${enemyAdc} : ${wr}% WR`,
      long: `Tu domines ${enemyAdc} avec ${game.champion} (${wr}% sur ${matchupGames.length} games). Exploite cet avantage de lane.`,
      context: { wr, games: matchupGames.length }
    };
  }
  return null;
}

// ============================================================
// F5 — Synergie support (TES games)
// ============================================================

function detectSupportSynergy(game, context) {
  const support = game.support;
  if (!support || !game.champion) return null;

  const allGames = context?.allGames;
  if (!Array.isArray(allGames) || allGames.length < 4) return null;

  const duoGames = allGames.filter(g =>
    !isCancelled(g) &&
    g.champion === game.champion &&
    g.support === support
  );
  if (duoGames.length < 4) return null;

  const wins = duoGames.filter(g => g.result === 'W').length;
  const wr = Math.round((wins / duoGames.length) * 100);

  if (wr >= 65) {
    return {
      id: 'F5_strong_synergy',
      category: 'F',
      severity: 'positive',
      title: 'Duo qui marche',
      short: `${game.champion} + ${support} : ${wr}% WR`,
      long: `Ton duo ${game.champion} + ${support} a ${wr}% de winrate sur ${duoGames.length} games. Combo à privilégier quand tu peux.`,
      context: { wr, games: duoGames.length }
    };
  }
  if (wr <= 30) {
    return {
      id: 'F5_weak_synergy',
      category: 'F',
      severity: 'info',
      title: 'Duo peu efficace',
      short: `${game.champion} + ${support} : ${wr}% WR`,
      long: `Ton duo ${game.champion} + ${support} n'a que ${wr}% sur ${duoGames.length} games. Pas forcément à éviter, mais à surveiller.`,
      context: { wr, games: duoGames.length }
    };
  }
  return null;
}

// ============================================================
// API publique
// ============================================================

export function analyzeItems(game, context = {}) {
  const out = [];
  const lateBoots = detectLateBoots(game, context);
  if (lateBoots) out.push(lateBoots);
  const coreItem = detectCoreItemTiming(game, context);
  if (coreItem) out.push(coreItem);
  const noCtrlWard = detectNoControlWard(game);
  if (noCtrlWard) out.push(noCtrlWard);
  const buildVsComp = detectBuildVsComp(game, context);
  if (buildVsComp) out.push(buildVsComp);
  return out;
}

export function analyzeVision(game, context = {}) {
  const out = [];
  const wards = detectWardsPlaced(game, context);
  if (wards) out.push(wards);
  const visionScore = detectVisionScoreVsBaseline(game, context);
  if (visionScore) out.push(visionScore);
  return out;
}

export function analyzeLevels(game, context = {}) {
  return detectLevelSpikes(game) || [];
}

export function analyzeDraft(game, context = {}) {
  const out = [];
  const matchup = detectMatchupVsBaseline(game, context);
  if (matchup) out.push(matchup);
  const synergy = detectSupportSynergy(game, context);
  if (synergy) out.push(synergy);
  return out;
}
